import { useEffect, useRef } from 'react';
import { useAuth } from 'auth/AuthProvider';
import { HttpClient } from 'apiclient/ApiClient';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
// Keyed per user, not per browser: the key used to be global, so the first account
// to sign in each day silenced the heartbeat for every other account on that
// machine — and the backend arms follow-up letters and records teacher activity
// from this call (funexpected/futuramath#832). Shared staff-room computers made
// that the normal case, not an edge case.
const lastActivityKey = (uid: string) => `last_activity_timestamp:${uid}`;

export default function useActivityTracking() {
    const auth = useAuth();
    const client = HttpClient();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!auth.session) {
            return;
        }
        const storageKey = lastActivityKey(auth.session.uid);

        const trackActivity = async () => {
            try {
                await client.post('/teachers/activity', {});
                localStorage.setItem(storageKey, Date.now().toString());
            } catch (err) {
                // Silently fail - activity tracking is not critical
                console.warn('Failed to track activity:', err);
            }
        };

        const shouldTrackActivity = () => {
            const lastActivity = localStorage.getItem(storageKey);
            if (!lastActivity) {
                return true;
            }
            const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
            return timeSinceLastActivity >= ONE_DAY_MS;
        };

        // Track on mount if needed
        if (shouldTrackActivity()) {
            trackActivity();
        }

        // Set up interval to check once per hour if we need to track
        // (checking more frequently than once per day to handle edge cases)
        intervalRef.current = setInterval(() => {
            if (shouldTrackActivity()) {
                trackActivity();
            }
        }, 60 * 60 * 1000); // Check every hour

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [auth.session, client]);
}

